/* =============================================================================
 *  feedActions — the behaviour layer for the Feed + Wall.
 *
 *  PROGRAMMATIC-FIRST: the common path is free + instant, not an LLM call.
 *    • openItem(item)  → opens the existing NATIVE modal/panel (job workspace,
 *                        invoice/bill view, email panel) — the same surfaces the
 *                        rest of the app uses, re-looked-up live by id.
 *    • setJobStatus()  → a direct Firestore write (the inline status dropdown).
 *    • ask()/askAndrei → Andrei, but only as a DELIBERATE secondary action (the
 *                        "✦" affordance / briefing), never the default click.
 *
 *  Hosts the modals itself (one of each, single-open) and returns them as `modals`
 *  for the surface to render once. Reuses the exact open/lookup pattern from the
 *  terminal AttentionQueue. Assumes Invoice/Bills providers above + the app-level
 *  AppData / EmailPanel / Approvals / AndreiChat contexts.
 * ============================================================================= */

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAppData } from '../../providers/AppDataProvider';
import { useInvoice } from '../../pages/invoices/contexts/InvoiceContext';
import { useBills } from '../../pages/bills/contexts/BillsContext';
import { useEmailPanel } from '../../pages/mail/contexts/EmailPanelContext';
import { useApprovals } from '../../contexts/ApprovalsContext';
import { useAndreiChat } from '../../contexts/AndreiChatContext';
import JobWorkspaceModal from '../../pages/job-summary/JobWorkspaceModal';
import InvoiceViewModal from '../../pages/invoices/components/InvoiceViewModal';
import BillViewModal from '../../pages/bills/components/BillViewModal';
import { markEmailRead, isGraphWriteForbiddenNow, OWNER_ONLY_REASON } from '../../pages/mail/utils/markEmailRead';
import { FeedItem } from './feedModel';

// Canonical job statuses (matches the JobStatus union + the app's status
// dropdowns), ordered the way the dashboard's job dropdown orders them.
export const JOB_STATUS_OPTIONS = [
  'Active', 'Open', 'Pending', 'Awaiting Client Response', 'Overdue', 'Closed', 'Cancelled', 'Archived',
];

// Short display labels keep the inline <select> narrow (the full value is still
// what's written) — so a long status can't overflow a small wall tile.
const STATUS_LABEL: Record<string, string> = { 'Awaiting Client Response': 'Awaiting' };
const statusLabel = (s: string): string => STATUS_LABEL[s] || s;

/**
 * Inline job-status dropdown — a real <select> (free, instant, accessible). Stops
 * propagation so changing status never also opens the workspace modal. Preserves
 * a non-canonical current status by prepending it as an option.
 */
export const JobStatusSelect: React.FC<{
  jobId: string; status: string; className?: string;
  onChange: (jobId: string, status: string) => void;
}> = ({ jobId, status, className, onChange }) => {
  const opts = JOB_STATUS_OPTIONS.includes(status) ? JOB_STATUS_OPTIONS : [status, ...JOB_STATUS_OPTIONS];
  return (
    <select
      className={className}
      value={status}
      aria-label="Job status"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); onChange(jobId, e.target.value); }}
    >
      {opts.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
    </select>
  );
};

export interface FeedActions {
  openItem: (item: FeedItem) => void;
  setJobStatus: (jobId: string, status: string) => void;
  markRead: (item: FeedItem) => void;
  askAndrei: (item: FeedItem) => void;
  ask: (prefill: string) => void;
  modals: React.ReactNode;
}

export function useFeedActions(): FeedActions {
  const { jobs } = useAppData();
  const { invoices } = useInvoice();
  const { bills } = useBills();
  const { openEmailPanel, setEmailPanelView, setDraftMode } = useEmailPanel();
  const { openApprovals } = useApprovals();
  const { openAndreiChat } = useAndreiChat();

  // Inline modal state — exactly one open at a time (mirrors AttentionQueue).
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [selectedBill, setSelectedBill] = useState<any>(null);

  // Open the underlying record in its native surface. Re-look-up by id so the
  // modal never shows the scored snapshot's stale data.
  const openItem = (item: FeedItem) => {
    const p = item.payload || {};
    switch (item.openKind) {
      case 'job': {
        const fresh = jobs.find((j: any) => j.id === p.id) ?? p.job ?? null;
        if (!fresh) { toast.info('That job is no longer available.'); return; }
        setSelectedJob(fresh); return;
      }
      case 'invoice': {
        const fresh = invoices.find((i: any) => i.id === p.id) ?? p.invoice ?? null;
        if (!fresh) { toast.info('That invoice is no longer available.'); return; }
        setSelectedInvoice(fresh); return;
      }
      case 'bill': {
        const fresh = bills.find((b: any) => b.id === p.id) ?? p.bill ?? null;
        if (!fresh) { toast.info('That bill is no longer available.'); return; }
        setSelectedBill(fresh); return;
      }
      case 'email': { if (p.email) openEmailPanel('viewEmail', p.email); return; }
      case 'draft': { if (p.draft) { setDraftMode('edit', p.draft); setEmailPanelView('editDraft'); } return; }
      case 'approval': { openApprovals(); return; }
      default: return;
    }
  };

  // Direct status write — free + instant. Backend firestore triggers handle any
  // side-effects on the jobs/{id} write; no need to route a status change to Andrei.
  const setJobStatus = (jobId: string, status: string) => {
    if (!jobId) { toast.error("Couldn't update job — missing id"); return; }
    if ((jobs.find(job => job.id === jobId) as any)?.support) {
      toast.info('Monthly support status follows its required duties. Open the job to update a duty.');
      return;
    }
    (async () => {
      try {
        await updateDoc(doc(db, 'jobs', jobId), { status, lastEdited: new Date().toISOString() });
        toast.success(`Status → ${status}`);
      } catch (e) {
        console.error('[feedActions] job status update failed:', e);
        toast.error("Couldn't update job status");
      }
    })();
  };

  // Mark an email thread read — programmatic, idempotent. Clears every message in
  // the collapsed thread so the card disappears (the emails snapshot recomputes).
  const markRead = (item: FeedItem) => {
    const emails: any[] = Array.isArray(item.payload?.threadEmails) && item.payload.threadEmails.length
      ? item.payload.threadEmails
      : (item.payload?.email ? [item.payload.email] : []);
    if (!emails.length) { toast.info('Nothing to mark read.'); return; }
    (async () => {
      try {
        // markEmailRead resolves TRUE only when Firestore AND Outlook both took the
        // change (it rolls back on failure). Silent so a thread of N messages can't
        // spawn N failure toasts - we report ONE honest summary instead.
        const results = await Promise.all(emails.map((e) => markEmailRead(e, { silent: true })));
        const failed = results.filter((ok) => !ok).length;
        if (failed === 0) {
          toast.success(emails.length > 1 ? `${emails.length} messages marked read` : 'Marked read');
        } else if (isGraphWriteForbiddenNow()) {
          // Owner-only endpoint (the second app user): say why, once, instead of
          // a generic failure that invites retrying something that cannot work.
          toast.error(`Couldn't mark read: ${OWNER_ONLY_REASON}`);
        } else {
          toast.error(emails.length > 1 ? `${failed} of ${emails.length} couldn't be marked read` : "Couldn't mark read");
        }
      } catch (e) {
        console.error('[feedActions] mark read failed:', e);
        toast.error("Couldn't mark read");
      }
    })();
  };

  const askAndrei = (item: FeedItem) => {
    openAndreiChat({
      prefill: item.andreiPrefill,
      context: item.andreiContextKind && item.payload?.id
        ? { type: item.andreiContextKind as any, id: String(item.payload.id) }
        : null,
    });
  };
  const ask = (prefill: string) => openAndreiChat({ prefill, context: null });

  const modals = (
    <>
      <JobWorkspaceModal show={!!selectedJob} job={selectedJob} onHide={() => setSelectedJob(null)} />
      <InvoiceViewModal show={!!selectedInvoice} invoice={selectedInvoice} onHide={() => setSelectedInvoice(null)} />
      <BillViewModal show={!!selectedBill} bill={selectedBill} onHide={() => setSelectedBill(null)} />
    </>
  );

  return { openItem, setJobStatus, markRead, askAndrei, ask, modals };
}
